import {call, put, select, takeLatest} from "redux-saga/effects";
import * as Routines from "../routines";
import * as Api from '../api';
import {UserWithCustomer} from "../../Models/UserWithCustomer";
import {Role} from "../../Models/Role";
import {LdapUser} from "../../Models/LdapUser";
import {mapLdapUsers} from "../../Users/utils";
import {DeletedUser} from "../../Models/User";
import {accessToCustomer} from "../../../Policy/rightsUtil";
import {PolicyNameEnum} from "../../../PolicyNameEnum";
import {feedbackFailure} from "../../../Feedback/reducer";
import { SiteTypeEnum } from "../../../SiteTypeEnum";
import { selectAppData, selectCurrentContext, selectCustomers } from "@Safemate/Store/selectors";
import { ICustomerWithPolicy } from "@Safemate/Model/Customer";
import { GlobalRoles } from "@Safemate/GlobalRoles";
import { AppAction, AppData } from "@Safemate/Store/types";
import { loadFilters, PersistedFilters } from "../utils";
import { setFilters } from "../actions";
import { getFilters } from "../selectors";
import { FilterState } from "../types";

export interface Initialize{
  siteType: SiteTypeEnum;
}

function* initialize(action: AppAction<Initialize>){
  const currentContext: SiteTypeEnum = yield select(selectCurrentContext);
  try {

    const appData: AppData = yield select(selectAppData);
    const roleId = appData.activeRoles[0].id;
    const userId = appData.user.id;
    const loadedFilters: PersistedFilters = loadFilters(roleId, userId);
    
    yield put(setFilters(loadedFilters));
    
    if(currentContext !== SiteTypeEnum.AHP){

      const customers: ICustomerWithPolicy[] = yield select(selectCustomers);


      for(let i = 0; i < customers.length; i++){
        if(accessToCustomer(customers[i].policies, PolicyNameEnum.DELETED_USERS, [GlobalRoles.SUPERFIRM])){
          yield put(Routines.initializeDeletedUsers());
          break;
        }
      }

      yield put(Routines.initializeLdapUsers());
    }

    yield put(Routines.initializeRoles());
    yield put(Routines.initializeUsers());
    yield put(Routines.initialize.success());
  }
  catch(e){
    yield put(Routines.initialize.failure());
  }
  yield put(Routines.initialize.fulfill());
}

function* initializeUsers(action: AppAction<SiteTypeEnum>){
  const currentContext: SiteTypeEnum = yield select(selectCurrentContext);
  try{
    const users: Array<UserWithCustomer> = yield call(Api.getUsers, currentContext);

    const filters: FilterState = yield select(getFilters);
    const filterMatch = users.some(user => {
      const matchInSubOrgs = user.subOrgs.some(suborg => 
        filters.customer.includes(suborg.custId)
      );

      const matchInTempSubOrgs = user.temporarySubOrgs.some(suborg => 
        filters.customer.includes(suborg.custId)
      );

      return matchInSubOrgs || matchInTempSubOrgs;
    });
    yield put(Routines.initializeUsers.success({users, clearFilter: !filterMatch}));
  }
  catch(e){
    feedbackFailure(e);
    yield put(Routines.initializeUsers.failure());
  }
}

function* initializeDeletedUsers(action: AppAction<null>){
  try{
    const deletedUsers: Array<DeletedUser> = yield call(Api.getDeletedUsers)
    yield put(Routines.initializeDeletedUsers.success(deletedUsers))
  }
  catch(e){
    feedbackFailure(e);
    yield put(Routines.initializeDeletedUsers.failure());
  }
}

function* initializeLdapUsers(action: AppAction<any>){
  try{
    const ldapUsers: Array<LdapUser> = yield call(Api.getLdapUsers);
    yield put(Routines.initializeLdapUsers.success(mapLdapUsers(ldapUsers)));
  }
  catch(e){
    feedbackFailure(e);
    yield put(Routines.initializeLdapUsers.failure());
  }
}

function* initializeRoles(action: AppAction<any>){
  try{
    const roles: Array<Role> = yield call(Api.getRoles);
    const filters: FilterState = yield select(getFilters);
    const filterMatch = roles.find(role => filters.role && filters.role.includes(role.id));
    yield put(Routines.initializeRoles.success({roles, clearFilter: !filterMatch }));
  }
  catch(e){
    feedbackFailure(e);
    yield put(Routines.initializeRoles.failure());
  }
}

function* initializeAdminRole(action: AppAction<null>){
  try{
    const admin: Role = yield call(Api.getAdminRole);
    yield put(Routines.initializeAdminRole.success(admin));
  }
  catch(e){
    feedbackFailure(e);
    yield put(Routines.initializeAdminRole.failure());
  }
}


function* initializationSaga(){
  yield takeLatest(Routines.initialize.TRIGGER, initialize);
  yield takeLatest(Routines.initializeUsers.TRIGGER, initializeUsers);
  yield takeLatest(Routines.initializeDeletedUsers.TRIGGER, initializeDeletedUsers);
  yield takeLatest(Routines.initializeLdapUsers.TRIGGER, initializeLdapUsers);
  yield takeLatest(Routines.initializeRoles.TRIGGER, initializeRoles);
  yield takeLatest(Routines.initializeAdminRole.TRIGGER, initializeAdminRole);
  yield takeLatest(Routines.deleteUser.FULFILL, initializeDeletedUsers);
  yield takeLatest(Routines.restoreUser.FULFILL, initializeUsers);
}

export default initializationSaga;
